/* ---------------------------------------------------------------------------
   Provisional → Non-Provisional Patent Deadline Calculator — engine + copy.

   Pure, deterministic, zero-AI. Given a US provisional filing date it computes:
     - the 12-month non-provisional (utility) deadline           [35 U.S.C. § 119(e)]
     - a weekend / federal-holiday roll to the next business day  [37 CFR 1.7]
     - the parallel 12-month Paris / PCT foreign-priority deadline [Paris Art. 4]
     - the ~2-month (14-month) restoration window                 [37 CFR 1.78(b)]
     - a days/months countdown and an urgency state

   Every rule here is sourced (see STATUTE + USPTO_SOURCE_URL). The one edge with
   no authoritative USPTO statement is the leap-day anniversary (a Feb 29 filing
   has no Feb 29 the next year): we take the conservative earlier date, Feb 28 —
   see addMonths below. Everything else is statutory and cited.

   ALL date math is timezone-safe: dates are treated as plain calendar days at
   LOCAL midnight (year/month/day), never parsed from an ISO string via
   `new Date("2026-01-15")` (which is UTC-parsed and shifts the day west of GMT).
   So the same filing date yields the identical deadline for every viewer.
--------------------------------------------------------------------------- */

export type Plan = 'us' | 'intl';
export type DeadlineState =
  | 'ample' // > 90 days out
  | 'approaching' // 31–90 days
  | 'urgent' // 1–30 days
  | 'today' // 0 days
  | 'restoration' // deadline passed, within the ~2-month restore window
  | 'lapsed'; // past the restore window

const DAY = 86_400_000;
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Normalize any Date to local midnight (drops time-of-day). */
export function d0(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
/**
 * Add whole calendar months, anniversary-style. When the target month is
 * shorter than the source day (e.g. Jan 31 → Feb, or a Feb 29 filing + 12
 * months landing on a non-leap February), clamp to the LAST day of the target
 * month. For the 12-month deadline the only case this hits is Feb 29 → Feb 28,
 * which we take deliberately as the conservative (earlier) deadline; the USPTO
 * has no published rule for the leap-day anniversary.
 */
export function addMonths(d: Date, n: number): Date {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const t = new Date(y, m + n, day);
  // If the day overflowed into the next month, roll back to the last day of the
  // intended month (day 0 of month+1 = last day of month).
  return t.getDate() !== day ? new Date(y, m + n + 1, 0) : t;
}

/** nth weekday (0=Sun..6=Sat) of a month, 1-indexed; or null. */
function nthWeekday(y: number, m: number, wd: number, n: number): Date {
  let count = 0;
  for (let dd = 1; dd <= 31; dd++) {
    const t = new Date(y, m, dd);
    if (t.getMonth() !== m) break;
    if (t.getDay() === wd && ++count === n) return t;
  }
  return new Date(y, m, 1); // unreachable for valid inputs
}
/** last weekday of a month. */
function lastWeekday(y: number, m: number, wd: number): Date {
  let r = new Date(y, m, 1);
  for (let dd = 1; dd <= 31; dd++) {
    const t = new Date(y, m, dd);
    if (t.getMonth() !== m) break;
    if (t.getDay() === wd) r = t;
  }
  return r;
}

/**
 * US federal holidays for a given year, computed algorithmically (never a
 * hardcoded per-year table, so it works for any filing date the user enters).
 * Fixed-date holidays use the OPM "observed" shift (Sat → prior Fri, Sun → next
 * Mon), matching when USPTO is actually closed.
 */
function fedHolidays(year: number): Date[] {
  const observed = (d: Date): Date => {
    const w = d.getDay();
    if (w === 0) return addDays(d, 1); // Sunday → Monday
    if (w === 6) return addDays(d, -1); // Saturday → Friday
    return d;
  };
  return [
    observed(new Date(year, 0, 1)), // New Year's Day
    nthWeekday(year, 0, 1, 3), // MLK Jr. Day — 3rd Mon Jan
    nthWeekday(year, 1, 1, 3), // Presidents' Day — 3rd Mon Feb
    lastWeekday(year, 4, 1), // Memorial Day — last Mon May
    observed(new Date(year, 5, 19)), // Juneteenth
    observed(new Date(year, 6, 4)), // Independence Day
    nthWeekday(year, 8, 1, 1), // Labor Day — 1st Mon Sep
    nthWeekday(year, 9, 1, 2), // Columbus Day — 2nd Mon Oct
    observed(new Date(year, 10, 11)), // Veterans Day
    nthWeekday(year, 10, 4, 4), // Thanksgiving — 4th Thu Nov
    observed(new Date(year, 11, 25)), // Christmas Day
  ].map(d0);
}

function isWeekend(d: Date): boolean {
  const w = d.getDay();
  return w === 0 || w === 6;
}
function isFederalHoliday(d: Date): boolean {
  const t = d0(d).getTime();
  return fedHolidays(d.getFullYear()).some((h) => h.getTime() === t);
}
/** Roll a date forward to the next business day (37 CFR 1.7 / 35 U.S.C. § 21(b)). */
export function rollToBusinessDay(d: Date): Date {
  let x = d0(d);
  while (isWeekend(x) || isFederalHoliday(x)) x = addDays(x, 1);
  return x;
}

/** "Mon, May 17, 2027" — weekday included so the holiday roll reads clearly. */
export function fmtLong(d: Date): string {
  return `${WD[d.getDay()]}, ${MO[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
/** "May 17, 2027" */
export function fmtShort(d: Date): string {
  return `${MO[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
/** "MM / DD / YYYY" for the input field display. */
export function fmtField(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)} / ${p(d.getDate())} / ${d.getFullYear()}`;
}
/** Whole months between two dates (floor). Used for the calm "N months" figure. */
export function monthsBetween(a: Date, b: Date): number {
  let m = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (addMonths(a, m) > b) m--;
  return m;
}

/** Parse "YYYY-MM-DD" into a LOCAL Date (timezone-safe), or null. */
export function parseISODate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2];
  const d = +m[3];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, mo - 1, d);
  // Reject overflow (e.g. Feb 31 → Mar 3).
  if (date.getMonth() !== mo - 1 || date.getDate() !== d) return null;
  return date;
}
/** Date → "YYYY-MM-DD" (local). */
export function toISODate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// --- Single source of truth for the legal references shown across the page ---
export const STATUTE = {
  provisional: '35 U.S.C. § 111(b)',
  nonProvisional: '35 U.S.C. § 119(e) · 37 CFR 1.78',
  foreign: 'Paris Conv. Art. 4 · PCT Art. 8',
  restoration: '37 CFR 1.78(b) · 1.55(c)',
  roll: '37 CFR 1.7',
} as const;

export const USPTO_SOURCE_URL =
  'https://www.uspto.gov/web/offices/pac/mpep/s201.html';

// The example docket shown before the user enters a date (specimen state).
export const EXAMPLE_MONTHS_AGO = 4;

// --- Urgency state presentation. Colors reference real brand + verdict tokens.
// `color` / `fill` are applied inline; `tint` is the answer-block background.
// `barBg` / `barText` drive the mobile sticky urgency bar.
export interface StateConfig {
  word: string;
  color: string;
  fill: string;
  tint: string;
  barBg: string;
  barText: string;
  sublabel: string;
}

export const STATES: Record<DeadlineState, StateConfig> = {
  ample: {
    word: 'On track',
    color: 'var(--heartwood)',
    fill: 'var(--heartwood)',
    tint: 'transparent',
    barBg: 'var(--heartwood)',
    barText: 'var(--parchment)',
    sublabel:
      'Plenty of runway. Use it to search the prior art and draft strong claims before you file.',
  },
  approaching: {
    word: 'Approaching',
    color: 'var(--vd-caution-ink)',
    fill: 'var(--vd-caution-fill)',
    tint: 'color-mix(in oklch, var(--vd-caution-fill) 13%, #fff)',
    barBg: 'var(--vd-caution-fill)',
    barText: '#3a2a08',
    sublabel:
      'Time to bring in a patent practitioner. A solid non-provisional takes weeks to draft, not days.',
  },
  urgent: {
    word: 'File soon',
    color: 'var(--oxblood)',
    fill: 'var(--oxblood)',
    tint: 'color-mix(in oklch, var(--oxblood) 8%, #fff)',
    barBg: 'var(--oxblood)',
    barText: 'var(--parchment)',
    sublabel:
      'File the non-provisional now. Miss this date and you lose the provisional\u2019s priority date, unless you can restore it.',
  },
  today: {
    word: 'Due today',
    color: 'var(--oxblood)',
    fill: 'var(--oxblood)',
    tint: 'color-mix(in oklch, var(--oxblood) 10%, #fff)',
    barBg: 'var(--oxblood)',
    barText: 'var(--parchment)',
    sublabel: 'Today is the deadline. File the non-provisional now to keep your priority date.',
  },
  restoration: {
    word: 'Deadline passed \u00b7 restoration window open',
    color: 'var(--oxblood)',
    fill: 'var(--oxblood)',
    tint: 'color-mix(in oklch, var(--oxblood) 10%, #fff)',
    barBg: 'var(--oxblood)',
    barText: 'var(--parchment)',
    sublabel:
      'The 12-month deadline has passed, but a narrow path remains: file within about two months, with a petition showing the delay was unintentional. It isn\u2019t guaranteed. Talk to a practitioner today.',
  },
  lapsed: {
    word: 'Priority window closed',
    color: 'var(--oxblood)',
    fill: 'color-mix(in oklch, var(--ink) 40%, var(--parchment))',
    tint: 'color-mix(in oklch, var(--ink) 5%, #fff)',
    barBg: 'var(--ink)',
    barText: 'var(--parchment)',
    sublabel:
      'Both the 12-month deadline and the restoration window are closed. The provisional can no longer anchor a priority claim. A practitioner can tell you what\u2019s still protectable.',
  },
};

export interface Docket {
  state: DeadlineState;
  cfg: StateConfig;
  /** Countdown parts: an optional prefix, the value, and an optional unit. */
  pre: string;
  value: string | number;
  unit: string;
  filing: Date;
  plan: Plan;
  rawDeadline: Date; // the raw 12-month anniversary, before the business-day roll
  deadline: Date; // the rolled non-provisional deadline
  rolled: boolean; // true when the anniversary landed on a weekend/holiday
  restoreClose: Date; // deadline + ~2 months (the 14-month outer bound)
  days: number; // whole days from today to the deadline
  rdays: number; // whole days from today to restoreClose
  dlFrac: number; // 0–100: deadline position on the timeline
  tdFrac: number; // 0–100: today's position on the timeline
  passed: boolean; // restoration or lapsed
  restoreHot: boolean; // restoration only (lights up the restore row)
}

/**
 * The whole computation. `now` is injected so the caller can re-derive on a
 * minute tick / tab-focus without the module reading the clock itself.
 */
export function computeDocket(filing: Date, plan: Plan, now: Date = new Date()): Docket {
  const today = d0(now);
  const rawDeadline = addMonths(filing, 12);
  const deadline = rollToBusinessDay(rawDeadline);
  const rolled = deadline.getTime() !== d0(rawDeadline).getTime();
  const restoreClose = rollToBusinessDay(addMonths(filing, 14));

  const days = Math.round((d0(deadline).getTime() - today.getTime()) / DAY);
  const rdays = Math.round((d0(restoreClose).getTime() - today.getTime()) / DAY);

  let state: DeadlineState;
  if (days > 90) state = 'ample';
  else if (days >= 31) state = 'approaching';
  else if (days >= 1) state = 'urgent';
  else if (days === 0) state = 'today';
  else state = rdays >= 0 ? 'restoration' : 'lapsed';

  let pre = '';
  let value: string | number = '';
  let unit = '';
  if (state === 'ample') {
    value = monthsBetween(today, deadline);
    unit = value === 1 ? 'month' : 'months';
  } else if (state === 'approaching' || state === 'urgent') {
    value = days;
    unit = days === 1 ? 'day' : 'days';
  } else if (state === 'today') {
    value = 'Today';
  } else if (state === 'restoration') {
    pre = 'Restoration closes in';
    value = rdays;
    unit = rdays === 1 ? 'day' : 'days';
  } else {
    value = 'Closed';
  }

  const span = (d0(restoreClose).getTime() - d0(filing).getTime()) / DAY;
  const dlFrac = Math.min(100, ((d0(deadline).getTime() - d0(filing).getTime()) / DAY / span) * 100);
  const tdFrac = Math.max(
    0,
    Math.min(100, ((today.getTime() - d0(filing).getTime()) / DAY / span) * 100),
  );

  return {
    state,
    cfg: STATES[state],
    pre,
    value,
    unit,
    filing,
    plan,
    rawDeadline,
    deadline,
    rolled,
    restoreClose,
    days,
    rdays,
    dlFrac,
    tdFrac,
    passed: state === 'restoration' || state === 'lapsed',
    restoreHot: state === 'restoration',
  };
}
